import { Badge, ButtonGroup, Dropdown, ListGroup } from "react-bootstrap";
import {
    FaDatabase,
    FaEllipsisV,
    FaEye,
    FaEyeSlash,
    FaFileAlt,
    FaLink,
} from "react-icons/fa";
import { useNavigate } from "react-router";

import * as api from "../../api/api";
import { getFirebaseConsoleUrl } from "../../api/firestoreBaseApi";
import { ResultDocument } from "../../api/firestoreWrappers.generated";
import urls from "../../urls";
import TimeDisplay from "../TimeDisplay";

export function ResultAdminMenu({
    result,
    file,
    showGoToMission = true,
    extraDropdownItems,
}) {
    const navigate = useNavigate();
    const toggleVisibility = () => {
        api.setResultPublished(
            result.organizationId,
            result.locationId,
            result.missionId,
            result.id,
            file.name,
            !file.published,
        );

        // send a notification request if this publishes the result file
        if (!file.published) {
            api.notifyUsingMissionHandler(
                result.organizationId,
                result.locationId,
                result.missionId,
            );
        }
    };

    const visitFirestore = () => {
        const resultPath = ResultDocument(
            result.organizationId,
            result.locationId,
            result.missionId,
            result.id,
        ).path;
        window.open(getFirebaseConsoleUrl(resultPath));
    };

    return (
        <Dropdown as={ButtonGroup} className="no-caret-dropdown">
            <Dropdown.Toggle
                as="span"
                role="button"
                tabIndex={0}
                variant="link"
                size="sm"
                className="p-0 text-muted shadow-none"
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.currentTarget.click();
                    }
                }}
            >
                <FaEllipsisV style={{ marginTop: "-2px" }} />
            </Dropdown.Toggle>

            <Dropdown.Menu align="end">
                <Dropdown.Item onClick={toggleVisibility}>
                    {file.published ? (
                        <>
                            <FaEyeSlash className="me-2" />
                            Unpublish file
                        </>
                    ) : (
                        <>
                            <FaEye className="me-2" />
                            Publish file
                        </>
                    )}
                </Dropdown.Item>
                <Dropdown.Item onClick={visitFirestore}>
                    <FaDatabase
                        className="me-2"
                        style={{ marginTop: "-2px" }}
                    />
                    Firestore
                </Dropdown.Item>
                {showGoToMission && (
                    <Dropdown.Item
                        onClick={() =>
                            navigate(
                                urls.locationMission(
                                    result.organizationId,
                                    result.locationId,
                                    result.missionId,
                                ),
                            )
                        }
                    >
                        <FaLink
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        Go to Mission
                    </Dropdown.Item>
                )}
                {typeof extraDropdownItems === "function"
                    ? extraDropdownItems({ result, file })
                    : extraDropdownItems}
            </Dropdown.Menu>
        </Dropdown>
    );
}

export function ResultListGroupItems({
    className,
    results,
    isAdmin,
    showTimestamp = true,
    showOrganizationName = false,
    organizationMap,
    showLocationName = false,
    locationMap,
    showGoToMission,
    extraDropdownItems,
}) {
    return (
        <>
            {results.flatMap((result) =>
                result.files
                    .filter(
                        (file) => file?.name && (isAdmin || !!file.published), // Filter out files without file name and unpublished files for non-admins
                    )
                    .map((file, fileIndex) => {
                        const rowKey = `${result.id}-${fileIndex}-${file.name}`;
                        const downloadresult = () =>
                            api.downloadResult(
                                result.organizationId,
                                result.locationId,
                                result.missionId,
                                result.id,
                                file.name,
                            );

                        const orgName =
                            showOrganizationName && organizationMap
                                ? organizationMap[result.organizationId]?.name
                                : null;

                        const locName =
                            showLocationName && locationMap
                                ? locationMap[result.locationId]?.name
                                : null;

                        let suffix = "";

                        if (orgName && locName) {
                            suffix = ` @ ${orgName} - ${locName}`;
                        } else if (orgName) {
                            suffix = ` @ ${orgName}`;
                        } else if (locName) {
                            suffix = ` @ ${locName}`;
                        }

                        return (
                            <ListGroup.Item key={rowKey} className="p-0">
                                <div
                                    className={`d-flex align-items-center justify-content-between ${className}`}
                                >
                                    {/* biome-ignore lint/a11y/useSemanticElements: This is intentionally only the actionable portion of a row that also contains separate controls. */}
                                    <div
                                        className="list-group-item-action d-flex align-items-center text-muted flex-grow-1"
                                        role="button"
                                        tabIndex={0}
                                        onClick={downloadresult}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                            ) {
                                                e.preventDefault();
                                                downloadresult();
                                            }
                                        }}
                                    >
                                        <FaFileAlt className="me-2" />
                                        <span>
                                            {file.label || file.name}
                                            {suffix}
                                        </span>
                                        {isAdmin && !file.published && (
                                            <FaEyeSlash className="ms-2" />
                                        )}
                                    </div>

                                    {(isAdmin || showTimestamp) && (
                                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                            {showTimestamp &&
                                                result.timestamp && (
                                                    <Badge pill bg="secondary">
                                                        <TimeDisplay
                                                            timestamp={
                                                                result.timestamp
                                                                    .seconds
                                                            }
                                                            format="HH:mm:ss"
                                                            organizationId={
                                                                result.organizationId
                                                            }
                                                            locationId={
                                                                result.locationId
                                                            }
                                                        />
                                                    </Badge>
                                                )}

                                            {isAdmin && (
                                                <ResultAdminMenu
                                                    result={result}
                                                    file={file}
                                                    showGoToMission={
                                                        showGoToMission
                                                    }
                                                    extraDropdownItems={
                                                        extraDropdownItems
                                                    }
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </ListGroup.Item>
                        );
                    }),
            )}
        </>
    );
}
